import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { projectService, Project } from '../services/projectService';
import { organizationService, OrgMember } from '../services/organizationService';
import '../styles/CreateProjectModal.css';

interface EditProjectModalProps {
  project: Project;
  onClose: () => void;
  onUpdated: (project: Project) => void;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({ project, onClose, onUpdated }) => {
  const { currentOrg, user } = useAuth();
  const [projectName, setProjectName] = useState(project.name);
  const [memberQuery, setMemberQuery] = useState('');
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<OrgMember[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;

    Promise.all([
      organizationService.getMembersWithProfiles(currentOrg.id),
      projectService.getProjectMembers(project.id),
    ]).then(([orgMembers, memberIds]) => {
      if (cancelled) return;
      setMembers(orgMembers);
      const idSet = new Set(memberIds);
      setSelectedMembers(orgMembers.filter(m => idSet.has(m.user_id)));
      setMembersLoaded(true);
    }).catch(() => {
      if (!cancelled) setMembersLoaded(true);
    }).finally(() => {
      if (!cancelled) setLoadingMembers(false);
    });

    return () => { cancelled = true; };
  }, [currentOrg, project.id]);

  const selectedIds = useMemo(() => new Set(selectedMembers.map(m => m.user_id)), [selectedMembers]);

  const matches = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return [];
    return members.filter(m =>
      !selectedIds.has(m.user_id) &&
      m.user_id !== user?.id &&
      ((m.full_name && m.full_name.toLowerCase().includes(q)) ||
       (m.email && m.email.toLowerCase().includes(q)))
    ).slice(0, 6);
  }, [memberQuery, members, selectedIds, user?.id]);

  const addMember = (member: OrgMember) => {
    setSelectedMembers(prev => [...prev, member]);
    setMemberQuery('');
    setSearchError(null);
  };

  const removeMember = (userId: string) => setSelectedMembers(prev => prev.filter(m => m.user_id !== userId));

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const q = memberQuery.trim().toLowerCase();
    if (!q) return;

    if (matches.length === 1) {
      addMember(matches[0]);
      return;
    }

    const exact = members.find(m =>
      !selectedIds.has(m.user_id) && m.user_id !== user?.id &&
      (m.email?.toLowerCase() === q || m.full_name?.toLowerCase() === q)
    );
    if (exact) {
      addMember(exact);
      return;
    }

    setSearchError(membersLoaded
      ? 'No member with that name or email was found in this organization.'
      : 'Still loading organization members — please try again.');
  };

  const handleSave = async () => {
    if (!projectName.trim()) { alert('Project name is required'); return; }
    setSaving(true);
    try {
      const updated = await projectService.updateProject(project.id, {
        name: projectName.trim(),
        memberUserIds: selectedMembers.map(m => m.user_id),
      });
      onUpdated(updated);
    } catch (err) {
      console.error('Error updating project:', err);
      alert('Failed to update project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pjm-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="pjm-modal" onClick={e => e.stopPropagation()}>
        <div className="pjm-modal-header">
          <h2>Edit project</h2>
          <button className="pjm-close-btn" onClick={onClose} disabled={saving}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="pjm-field">
          <label className="pjm-label">Project name <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            className="pjm-input"
            type="text"
            placeholder="e.g. Mobile App Revamp"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            autoFocus
          />
        </div>

        <div className="pjm-field">
          <label className="pjm-label">Members (search by name or email)</label>
          <div className="pjm-email-input-row" style={{ position: 'relative' }}>
            <input
              className="pjm-input"
              type="text"
              placeholder="Search organization members..."
              value={memberQuery}
              onChange={e => { setMemberQuery(e.target.value); setSearchError(null); }}
              onKeyDown={handleSearchSubmit}
            />
            {memberQuery.trim() && matches.length > 0 && (
              <div className="pjm-member-suggestions">
                {matches.map(m => (
                  <button type="button" key={m.user_id} className="pjm-member-suggestion" onClick={() => addMember(m)}>
                    <span className="pjm-member-name">{m.full_name || m.email}</span>
                    {m.full_name && <span className="pjm-member-email">{m.email}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {searchError && <p className="pjm-search-error">{searchError}</p>}
          {loadingMembers ? (
            <p className="pjm-search-error" style={{ color: 'var(--text-secondary, #6b7280)' }}>Loading members...</p>
          ) : selectedMembers.length > 0 && (
            <div className="pjm-email-chips">
              {selectedMembers.map(m => {
                const isSelf = m.user_id === user?.id;
                return (
                  <span key={m.user_id} className="pjm-email-chip">
                    {m.full_name || m.email}{isSelf ? ' (you)' : ''}
                    {!isSelf && (
                      <button type="button" onClick={() => removeMember(m.user_id)} aria-label={`Remove ${m.full_name || m.email}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="pjm-modal-footer">
          <button className="pjm-get-started-btn" onClick={handleSave} disabled={saving || !projectName.trim()}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProjectModal;
